import type { StorefrontCatalog, StorefrontCategoryDetail } from '@erp/shared';
import { ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { useStorefront } from '../../app/StorefrontLayout.js';
import { RTE_CONTENT_CSS } from '../../components/ui/index.js';
import { assetUrl, publicApi } from '../../lib/publicApi.js';
import { sanitizeHtml } from '../../lib/sanitizeHtml.js';

import { Breadcrumbs } from './Breadcrumbs.js';
import { pricingRecorder } from './buyerPricing.js';
import { ProductCard } from './ProductCard.js';
import { CategorySkeleton, ProductGridSkeleton } from './StorefrontSkeletons.js';
import { StoreImage } from './StoreImage.js';

const PAGE = 20;

export function StorefrontCategoryPage(): JSX.Element {
  const { t, i18n } = useTranslation('cms');
  const { slug, site } = useStorefront();
  const { catSlug = '' } = useParams();
  const [cat, setCat] = useState<StorefrontCategoryDetail | null>(null);
  const [data, setData] = useState<StorefrontCatalog | null>(null);
  const [status, setStatus] = useState<'loading' | 'ok' | 'notfound'>('loading');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  // Products refetch on every keystroke/page — keep the last grid on screen and
  // just dim it, instead of blanking back to skeletons mid-search.
  const [busy, setBusy] = useState(false);

  // A different category is a fresh browse — the previous search and page don't carry over.
  useEffect(() => { setQ(''); setOffset(0); }, [catSlug]);

  // The category and its products are fetched independently so searching/paging
  // never re-reads the category. Each guards its own late response: clicking A
  // then B used to let A's reply paint its hero and products under B's URL.
  useEffect(() => {
    let ignore = false;
    setStatus('loading'); setCat(null);
    void publicApi().get<StorefrontCategoryDetail>(`/${slug}/categories/${encodeURIComponent(catSlug)}`)
      .then((r) => { if (!ignore) { setCat(r.data); setStatus('ok'); } })
      .catch(() => { if (!ignore) setStatus('notfound'); });
    return () => { ignore = true; };
  }, [slug, catSlug, i18n.language]);

  const catId = cat?.id;
  useEffect(() => {
    if (!catId) { setData(null); return; }
    let ignore = false;
    const params: Record<string, string | number> = { category: catId, limit: PAGE, offset };
    if (q.trim()) params.q = q.trim();
    setBusy(true);
    // The response says which group + currency it was priced for — cache it so
    // the cart/checkout key off the shopper's currency, not the store's.
    const remember = pricingRecorder(slug);
    void publicApi().get<StorefrontCatalog>(`/${slug}/products`, { params })
      .then((r) => { if (!ignore) { setData(r.data); remember(r.data.pricing); } })
      .catch(() => { if (!ignore) setData({ items: [], total: 0, categories: [] }); })
      .finally(() => { if (!ignore) setBusy(false); });
    return () => { ignore = true; };
  }, [slug, catId, q, offset, i18n.language]);

  if (status === 'loading') return <CategorySkeleton />;
  if (status === 'notfound' || !cat) return <div className="py-16 text-center text-fg-muted">{t('store.category.notFound')}</div>;

  const heroImg = assetUrl(cat.imageUrl);
  const onSearch = (v: string): void => { setQ(v); setOffset(0); };

  return (
    <div className="flex flex-col gap-8">
      <style>{RTE_CONTENT_CSS}</style>
      <Helmet>
        <title>{`${cat.seoTitle ?? cat.name} · ${site.storeName}`}</title>
        {cat.seoDescription && <meta name="description" content={cat.seoDescription} />}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={cat.seoTitle ?? cat.name} />
        {cat.seoDescription && <meta property="og:description" content={cat.seoDescription} />}
        {(heroImg ?? assetUrl(site.logoUrl)) && <meta property="og:image" content={(heroImg ?? assetUrl(site.logoUrl))!} />}
      </Helmet>

      <Breadcrumbs
        items={[
          { label: t('store.home.crumb'), to: `/s/${slug}` },
          { label: t('store.shop.title'), to: `/s/${slug}/shop` },
          ...cat.breadcrumb.map((b) => ({ label: b.name, to: `/s/${slug}/category/${b.slug}` })),
          { label: cat.name },
        ]}
      />

      {/* Category hero */}
      <section className="relative overflow-hidden rounded-3xl border border-border bg-bg-subtle">
        <StoreImage src={cat.imageUrl} alt={cat.name} className="absolute inset-0 h-full w-full object-cover" />
        {heroImg && <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" aria-hidden />}
        <div className={`relative px-6 py-12 sm:px-10 sm:py-16 ${heroImg ? 'text-white' : ''}`}>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{cat.name}</h1>
          {cat.descriptionHtml && (
            <div className={`rte-content mt-2 max-w-2xl text-sm ${heroImg ? 'text-white/90' : 'text-fg-muted'}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(cat.descriptionHtml) }} />
          )}
        </div>
      </section>

      {cat.children.length > 0 && (
        <section className="flex flex-col gap-5">
          <h2 className="text-xl font-bold tracking-tight">{t('store.category.subcategories')}</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {cat.children.map((c) => {
              return (
                <Link key={c.id} to={`/s/${slug}/category/${c.slug}`} className="group relative aspect-[4/3] overflow-hidden rounded-2xl border border-border bg-bg-subtle">
                  <StoreImage src={c.imageUrl} alt={c.name} className="absolute inset-0 h-full w-full object-cover transition duration-300 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" aria-hidden />
                  <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                    <div className="truncate text-sm font-semibold drop-shadow-sm">{c.name}</div>
                    <div className="text-xs opacity-90">{t('store.category.count', { count: c.productCount })}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      <section className="flex flex-col gap-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-xl font-bold tracking-tight">{t('store.category.products')}</h2>
            {data && (
              <p className="mt-1 text-sm text-fg-muted">
                {t('store.shop.results', { count: data.total })}
                {/* The listing is descendant-inclusive server-side, so say so —
                    otherwise the count reads as a mismatch with the child cards. */}
                {cat.children.length > 0 && <> · {t('store.category.includesSubcategories')}</>}
              </p>
            )}
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute inset-y-0 start-3 my-auto h-4 w-4 text-fg-muted" aria-hidden />
            <input
              value={q}
              onChange={(e) => onSearch(e.target.value)}
              placeholder={t('store.category.search')}
              className="h-11 w-full rounded-full border border-border bg-bg-subtle ps-9 pe-4 text-sm outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20"
              aria-label={t('store.category.search')}
            />
          </div>
        </div>

        {!data ? (
          <ProductGridSkeleton count={PAGE} />
        ) : data.items.length === 0 ? (
          <div className="py-12 text-center text-fg-muted">
            {q.trim() ? (
              <>
                <p>{t('store.category.noMatch', { q: q.trim() })}</p>
                <button type="button" onClick={() => onSearch('')} className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-brand transition hover:opacity-80">
                  <X className="h-3.5 w-3.5" aria-hidden />{t('store.shop.clear')}
                </button>
              </>
            ) : t('store.shop.empty')}
          </div>
        ) : (
          <>
            <div className={`grid grid-cols-2 gap-5 transition-opacity sm:grid-cols-3 lg:grid-cols-4 ${busy ? 'opacity-60' : ''}`} aria-busy={busy}>
              {data.items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
            {data.total > PAGE && (() => {
              const pageCount = Math.ceil(data.total / PAGE);
              const current = Math.floor(offset / PAGE);
              const go = (p: number): void => setOffset(Math.min(Math.max(p, 0), pageCount - 1) * PAGE);
              const nums: Array<number | 'gap'> = [];
              for (let p = 0; p < pageCount; p++) {
                if (pageCount <= 7 || p === 0 || p === pageCount - 1 || Math.abs(p - current) <= 1) nums.push(p);
                else if (nums[nums.length - 1] !== 'gap') nums.push('gap');
              }
              const pill = (active: boolean): string =>
                `grid h-9 min-w-9 place-items-center rounded-full px-3 text-sm font-medium transition disabled:opacity-40 ${active ? 'bg-brand text-brand-contrast shadow-sm' : 'border border-border text-fg-muted hover:border-brand/40 hover:text-fg'}`;
              return (
                <nav className="flex items-center justify-center gap-2" aria-label={t('store.shop.pagination')}>
                  <button type="button" onClick={() => go(current - 1)} disabled={current === 0} className={pill(false)} aria-label={t('store.shop.prev')}>
                    <ChevronLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
                  </button>
                  {nums.map((n, i) => n === 'gap'
                    ? <span key={`gap-${i}`} className="px-1 text-fg-muted" aria-hidden>…</span>
                    : <button key={n} type="button" onClick={() => go(n)} aria-current={n === current ? 'page' : undefined} className={pill(n === current)}>{n + 1}</button>)}
                  <button type="button" onClick={() => go(current + 1)} disabled={current >= pageCount - 1} className={pill(false)} aria-label={t('store.shop.next')}>
                    <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
                  </button>
                </nav>
              );
            })()}
          </>
        )}
      </section>
    </div>
  );
}
